import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import "./StagingImportsPage.css";

function StagingImportsPage({ user }) {
    const [imports, setImports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [emailSearchRunning, setEmailSearchRunning] = useState(false);
    const [emailSearchStatus, setEmailSearchStatus] = useState("");
    const [editingItem, setEditingItem] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [uploadingId, setUploadingId] = useState(null);
    const [pendingUploadId, setPendingUploadId] = useState(null);
    const [actionStatus, setActionStatus] = useState("");

    const [lookups, setLookups] = useState({
        lodges: [],
        documentTypes: [],
        eventTypes: [],
        fileTypes: [],
        orderCodes: [],
    });

    const lookupOptions = useMemo(() => ({
        lodges: lookups.lodges.map((x) => ({ value: x.code, label: x.name })),
        documentTypes: lookups.documentTypes.map((x) => ({ value: x.code, label: x.description })),
        eventTypes: lookups.eventTypes.map((x) => ({ value: x.code, label: x.description })),
        fileTypes: lookups.fileTypes.map((x) => ({ value: x.code, label: x.description })),
        orderCodes: lookups.orderCodes.map((x) => ({ value: x.code, label: x.description })),
    }), [lookups]);

    const fetchImports = async () => {
        try {
            const { data } = await axios.get("/api/email-imports");
            setImports(data);
        } catch (err) {
            console.error("Failed to fetch staging imports:", err);
            alert("Failed to fetch staging imports.");
        } finally {
            setLoading(false);
        }
    };

    const fetchLookups = async () => {
        try {
            const [lodges, documentTypes, eventTypes, fileTypes, orderCodes] = await Promise.all([
                axios.get("/api/lodges"),
                axios.get("/api/document-types"),
                axios.get("/api/event-types"),
                axios.get("/api/file-types"),
                axios.get("/api/order-codes"),
            ]);

            setLookups({
                lodges: lodges.data || [],
                documentTypes: documentTypes.data || [],
                eventTypes: eventTypes.data || [],
                fileTypes: fileTypes.data || [],
                orderCodes: orderCodes.data || [],
            });
        } catch (err) {
            console.error("Failed to fetch lookup data:", err);
            alert("Failed to fetch dropdown data.");
        }
    };

    useEffect(() => {
        fetchImports();
        fetchLookups();
    }, []);

    const missing = (value) => !value || String(value).trim() === "";

    const normaliseStatus = (status) =>
        String(status || "pending")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");

    const statusLabel = (status) =>
        normaliseStatus(status)
            .split("_")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ") || "Pending";

    const importReceivedAt = (item) =>
        item.created_at || item.received_at || item.email_received_at || item.imported_at || item.updated_at;

    const isRecentlyAdded = (item) => {
        const value = importReceivedAt(item);
        if (!value) return normaliseStatus(item.import_status) === "new";
        const received = new Date(value).getTime();
        if (Number.isNaN(received)) return normaliseStatus(item.import_status) === "new";
        return Date.now() - received <= 7 * 24 * 60 * 60 * 1000;
    };

    const orderName = (code) =>
        lookups.orderCodes.find(o => o.code === code)?.description || code;

    const renderField = (value) => {
        if (missing(value)) return <span className="missing-field">Missing</span>;
        return value;
    };

    const cleanTitlePart = (value) =>
        String(value || "")
            .replace(/\s+/g, " ")
            .trim();

    const buildSubmissionTitle = (item = {}) => {
        const savedTitle = cleanTitlePart(item.submission_title);
        if (savedTitle) return savedTitle;

        const documentText = cleanTitlePart(item.document_text) || "Document";
        const lodgeText = cleanTitlePart(item.lodge_text);
        const meetingDate = cleanTitlePart(item.meeting_date);

        return [documentText, lodgeText, meetingDate].filter(Boolean).join(" - ");
    };

    const resolvedComplete = (item) =>
        item.resolved_scope_code &&
        item.resolved_constitution_code &&
        item.resolved_country_code &&
        item.resolved_order_code &&
        item.resolved_lodge_code &&
        item.resolved_document_type_code &&
        item.resolved_event_type_code &&
        item.resolved_file_type_code &&
        item.meeting_date &&
        item.description;

    const readyForUpload = (item) =>
        resolvedComplete(item) &&
        !missing(item.saved_path) &&
        normaliseStatus(item.import_status) !== "duplicate";

    const normalise = (value) =>
        String(value || "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, " ")
            .trim();

    const guessEventTypeCode = (eventText) => {
        const text = normalise(eventText);

        if (
            text.includes("stated communication") ||
            text.includes("regular stated communication") ||
            text.includes("stated communication meeting")
        ) {
            const match = lookups.eventTypes.find(
                (e) => normalise(e.description) === "meeting regular"
            );

            return match?.code || "";
        }

        return "";
    };

    const openEdit = (item) => {
        setEditingItem(item);
        setEditForm({
            submitted_by: item.submitted_by || item.source_email_from || "",
            lodge_text: item.lodge_text || "",
            document_text: item.document_text || "",
            meeting_date: item.meeting_date || "",
            event_type_text: item.event_type_text || "",
            description: item.description || "",
            keywords: item.keywords || "",
            submission_title: item.submission_title || buildSubmissionTitle(item),

            resolved_scope_code: item.resolved_scope_code || "LOD",
            resolved_constitution_code: item.resolved_constitution_code || "IC",
            resolved_country_code: item.resolved_country_code || "ZAF",
            resolved_order_code: item.resolved_order_code || "003",
            resolved_lodge_code: item.resolved_lodge_code || "",
            resolved_document_type_code: item.resolved_document_type_code || "",
            resolved_event_type_code:
                item.resolved_event_type_code ||
                guessEventTypeCode(item.event_type_text) ||
                "",
            resolved_file_type_code: item.resolved_file_type_code || "",
        });
    };

    const closeEdit = () => {
        setEditingItem(null);
        setEditForm({});
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const useGeneratedTitle = () => {
        setEditForm((prev) => ({
            ...prev,
            submission_title: buildSubmissionTitle({
                ...editingItem,
                ...prev,
                submission_title: "",
            }),
        }));
    };

    const handleResolvedChange = (field, selected) => {
        setEditForm((prev) => ({
            ...prev,
            [field]: selected?.value || "",
        }));
    };

    const selectedOption = (options, value) =>
        options.find((opt) => opt.value === value) || null;

    const saveEdit = async () => {
        try {
            await axios.put(`/api/email-imports/${editingItem.id}`, editForm);
            closeEdit();
            fetchImports();
        } catch (err) {
            console.error("Failed to update staging import:", err);
            alert("Failed to update staging import.");
        }
    };

    const validateImport = async (item) => {
        try {
            const { data } = await axios.post(`/api/email-imports/${item.id}/validate`);

            if (data.valid) {
                alert("Validation successful. This item appears ready for import.");
            } else {
                const duplicateLines = (data.duplicates || []).map((doc) =>
                    `Duplicate: ${doc.title || doc.filename || doc.id}`
                );
                alert("Validation failed:\n\n" + [...(data.missing || []), ...duplicateLines].join("\n"));
            }

            fetchImports();
        } catch (err) {
            console.error("Failed to validate staging import:", err);
            alert("Failed to validate staging import.");
        }
    };

    const deleteImport = async (item) => {
        const confirmed = window.confirm(
            `Delete "${item.original_filename}"?\n\nThis will permanently remove both the staging record and the uploaded file.`
        );

        if (!confirmed) return;

        try {
            await axios.delete(`/api/email-imports/${item.id}`);
            setImports((prev) => prev.filter((i) => i.id !== item.id));
        } catch (err) {
            console.error("Failed to delete staging import:", err);
            alert("Failed to delete staging import.");
        }
    };

    const uploadImport = async (item) => {
        if (pendingUploadId !== item.id) {
            setPendingUploadId(item.id);
            setActionStatus(`Ready to upload ${item.original_filename}. Click Confirm upload to add it to the main archive.`);
            return;
        }

        setUploadingId(item.id);
        setActionStatus(`Uploading ${item.original_filename}...`);
        try {
            const { data } = await axios.post(`/api/email-imports/${item.id}/upload`);
            setActionStatus(data.message || "Uploaded successfully.");
            setPendingUploadId(null);
            fetchImports();
        } catch (err) {
            console.error("Failed to upload staging import:", err);
            const serverMessage =
                err.response?.data?.error ||
                err.response?.data?.details ||
                err.response?.data?.message;
            const details = err.response?.data?.details;
            const status = err.response?.status ? ` (${err.response.status})` : "";
            const message = `${serverMessage || `Failed to upload staging import${status}. Check the server logs for details.`}${details ? ` ${details}` : ""}`;
            setActionStatus(message);
            alert(message);
        } finally {
            setUploadingId(null);
        }
    };

    const runEmailSearchUpdate = async () => {
        setEmailSearchRunning(true);
        setEmailSearchStatus("Checking the archive mailbox for new attachments...");

        try {
            const { data } = await axios.post("/api/email-imports/run");
            setEmailSearchStatus(
                data.message ||
                `Email search update complete. ${data.inserted || 0} new staging imports added.`
            );
            await fetchImports();
        } catch (err) {
            console.error("Failed to run email search update:", err);
            const serverMessage =
                err.response?.data?.error ||
                err.response?.data?.details ||
                err.response?.data?.message;
            setEmailSearchStatus(serverMessage || "Email search update failed. Check the server logs.");
        } finally {
            setEmailSearchRunning(false);
        }
    };

    if (loading) {
        return (
            <div className="container3">
                <p>Loading staging imports...</p>
            </div>
        );
    }



    const lodgeName = (code) =>
        lookups.lodges.find(l => l.code === code)?.name || code;

    const documentName = (code) =>
        lookups.documentTypes.find(d => d.code === code)?.description || code;

    const eventName = (code) =>
        lookups.eventTypes.find(e => e.code === code)?.description || code;

    const fileTypeName = (code) =>
        lookups.fileTypes.find(f => f.code === code)?.description || code;

    const visibleImports = imports.filter(
        (item) => item.import_status !== "uploaded"
    );

    const statusCounts = visibleImports.reduce((acc, item) => {
        const key = normaliseStatus(item.import_status);
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});
    const newCount = visibleImports.filter(isRecentlyAdded).length;
    const readyCount = visibleImports.filter(readyForUpload).length;
    const needsMetadataCount = visibleImports.filter((item) => !readyForUpload(item)).length;

    return (
        <div className="container3">
            <div className="staging-page-header">
                <div>
                    <h2>Email Staging Imports</h2>

                    <p>
                        These documents were captured from the archive email account and are awaiting review
                        before being added to the main archive.
                    </p>
                </div>

                <div className="staging-header-actions">
                    <button
                        className="staging-btn staging-sync-btn"
                        onClick={runEmailSearchUpdate}
                        disabled={emailSearchRunning}
                    >
                        {emailSearchRunning ? "Updating..." : "Update Email Searches"}
                    </button>
                    {emailSearchStatus && (
                        <span className={`staging-sync-status ${emailSearchRunning ? "running" : ""}`}>
                            {emailSearchStatus}
                        </span>
                    )}
                    {actionStatus && (
                        <span className="staging-sync-status">
                            {actionStatus}
                        </span>
                    )}
                </div>
            </div>

            <div className="staging-summary">
                <div className="staging-summary-card">
                    <span>Total waiting</span>
                    <strong>{visibleImports.length}</strong>
                </div>
                <div className="staging-summary-card new">
                    <span>New / recent</span>
                    <strong>{newCount}</strong>
                </div>
                <div className="staging-summary-card ready">
                    <span>Ready to upload</span>
                    <strong>{readyCount}</strong>
                </div>
                <div className="staging-summary-card warning">
                    <span>Needs metadata</span>
                    <strong>{needsMetadataCount}</strong>
                </div>
                {Object.entries(statusCounts).map(([status, count]) => (
                    <span className={`status-badge status-${status}`} key={status}>
                        {statusLabel(status)}: {count}
                    </span>
                ))}
            </div>

            {visibleImports.length === 0 ? (
                <p>No staging imports found.</p>
            ) : (
                <div className="staging-table-wrapper">
                    <table className="staging-table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Status</th>
                            <th>File</th>
                            <th>Submission Title</th>
                            <th>Lodge</th>
                            <th>Document</th>
                            <th>Date</th>
                            <th>Event Type</th>
                            <th>Description</th>
                            <th>Submitted By</th>
                            <th>Sender</th>
                            <th>Actions</th>
                        </tr>
                        </thead>

                        <tbody>
                        {visibleImports.map((item) => (
                            <React.Fragment key={item.id}>
                                <tr
                                    className={
                                        [
                                            item.lodge_text,
                                            item.document_text,
                                            item.meeting_date,
                                            item.event_type_text,
                                            item.description,
                                        ].some(missing)
                                            ? "row-needs-metadata"
                                            : ""
                                    }
                                >
                                    <td>{item.id}</td>
                                    <td>
                                        <span className={`status-badge status-${normaliseStatus(item.import_status)}`}>
                                            {statusLabel(item.import_status)}
                                        </span>
                                        {isRecentlyAdded(item) && (
                                            <span className="new-import-badge">New</span>
                                        )}
                                    </td>
                                    <td>{item.original_filename}</td>
                                    <td className="submission-title-cell">
                                        {renderField(item.submission_title || buildSubmissionTitle(item))}
                                    </td>
                                    <td>{renderField(item.lodge_text)}</td>
                                    <td>{renderField(item.document_text)}</td>
                                    <td>{renderField(item.meeting_date)}</td>
                                    <td>{renderField(item.event_type_text)}</td>
                                    <td>{renderField(item.description)}</td>
                                    <td>{renderField(item.submitted_by || item.source_email_from)}</td>
                                    <td>{item.source_email_from}</td>
                                    <td>
                                        <div className="staging-actions">
                                            <a className="staging-btn" href={`/api/email-imports/${item.id}/file`}>
                                                View
                                            </a>

                                            <button type="button" className="staging-btn secondary" onClick={() => openEdit(item)}>
                                                Edit
                                            </button>

                                            <button type="button" className="staging-btn validate" onClick={() => validateImport(item)}>
                                                Validate
                                            </button>

                                            {readyForUpload(item) && (
                                                <button
                                                    type="button"
                                                    className={`staging-btn upload ${pendingUploadId === item.id ? "confirm" : ""}`}
                                                    onClick={() => uploadImport(item)}
                                                    disabled={uploadingId === item.id}
                                                >
                                                    {uploadingId === item.id
                                                        ? "Uploading..."
                                                        : pendingUploadId === item.id
                                                            ? "Confirm upload"
                                                            : "Upload"}
                                                </button>
                                            )}

                                            <button type="button" className="staging-btn danger" onClick={() => deleteImport(item)}>
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>

                                <tr className="resolved-row">
                                    <td></td>
                                    <td colSpan="11">
                                        <strong>Resolved upload metadata</strong>

                                        <div className="resolved-grid">

                                            <div className="resolved-title-preview">
                                                <strong>Submission Title:</strong>{" "}
                                                {item.submission_title || buildSubmissionTitle(item)}
                                            </div>

                                            <div>
                                                <strong>Scope:</strong> {item.resolved_scope_code || "LOD"}
                                            </div>

                                            <div>
                                                <strong>Constitution:</strong> {item.resolved_constitution_code || "IC"}
                                            </div>

                                            <div>
                                                <strong>Country:</strong> {item.resolved_country_code || "ZAF"}
                                            </div>

                                            <div>
                                                <strong>Order:</strong>{" "}
                                                {item.resolved_order_code
                                                    ? orderName(item.resolved_order_code)
                                                    : <span className="missing-field">Missing</span>}
                                            </div>

                                            <div>
                                                <strong>Lodge:</strong>{" "}
                                                {item.resolved_lodge_code
                                                    ? lodgeName(item.resolved_lodge_code)
                                                    : <span className="missing-field">Missing</span>}
                                            </div>

                                            <div>
                                                <strong>Document Type:</strong>{" "}
                                                {item.resolved_document_type_code
                                                    ? documentName(item.resolved_document_type_code)
                                                    : <span className="missing-field">Missing</span>}
                                            </div>

                                            <div>
                                                <strong>Event Type:</strong>{" "}
                                                {item.resolved_event_type_code
                                                    ? eventName(item.resolved_event_type_code)
                                                    : <span className="missing-field">Missing</span>}
                                            </div>

                                            <div>
                                                <strong>File Type:</strong>{" "}
                                                {item.resolved_file_type_code
                                                    ? fileTypeName(item.resolved_file_type_code)
                                                    : <span className="missing-field">Missing</span>}
                                            </div>

                                            <div>
                                                <strong>Staged File:</strong>{" "}
                                                {item.saved_path
                                                    ? "Ready"
                                                    : <span className="missing-field">Missing</span>}
                                            </div>

                                        </div>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editingItem && (
                <div className="staging-modal-backdrop">
                    <div className="staging-modal">
                        <h3>Edit Staging Metadata</h3>

                        <label>Submission Title</label>
                        <div className="submission-title-editor">
                            <input
                                name="submission_title"
                                value={editForm.submission_title || ""}
                                onChange={handleEditChange}
                                placeholder="Title saved to the main archive"
                            />
                            <button className="staging-btn secondary" type="button" onClick={useGeneratedTitle}>
                                Use Generated Title
                            </button>
                        </div>
                        <p className="staging-field-note">
                            This is the title that will be submitted to the main archive. The email template remains unchanged.
                        </p>

                        <label>Submitted By</label>
                        <input name="submitted_by" value={editForm.submitted_by} onChange={handleEditChange} />

                        <label>Lodge Text</label>
                        <input name="lodge_text" value={editForm.lodge_text} onChange={handleEditChange} />

                        <label>Document Text</label>
                        <input name="document_text" value={editForm.document_text} onChange={handleEditChange} />

                        <label>Meeting Date</label>
                        <input type="date" name="meeting_date" value={editForm.meeting_date} onChange={handleEditChange} />

                        <label>Event Type Text</label>
                        <input name="event_type_text" value={editForm.event_type_text} onChange={handleEditChange} />

                        <label>Description</label>
                        <textarea name="description" value={editForm.description} onChange={handleEditChange} />

                        <label>Keywords</label>
                        <input name="keywords" value={editForm.keywords} onChange={handleEditChange} />

                        <hr />

                        <h4>Resolved Upload Metadata</h4>

                        <label>Resolved Order</label>
                        <Select
                            options={lookupOptions.orderCodes}
                            value={selectedOption(lookupOptions.orderCodes, editForm.resolved_order_code)}
                            onChange={(selected) => handleResolvedChange("resolved_order_code", selected)}
                            placeholder="Select Order"
                            isClearable
                        />

                        <label>Resolved Lodge</label>
                        <Select
                            options={lookupOptions.lodges}
                            value={selectedOption(lookupOptions.lodges, editForm.resolved_lodge_code)}
                            onChange={(selected) => handleResolvedChange("resolved_lodge_code", selected)}
                            placeholder="Select Lodge"
                            isClearable
                        />

                        <label>Resolved Document Type</label>
                        <Select
                            options={lookupOptions.documentTypes}
                            value={selectedOption(lookupOptions.documentTypes, editForm.resolved_document_type_code)}
                            onChange={(selected) => handleResolvedChange("resolved_document_type_code", selected)}
                            placeholder="Select Document Type"
                            isClearable
                        />

                        <label>Resolved Event Type</label>
                        <Select
                            options={lookupOptions.eventTypes}
                            value={selectedOption(lookupOptions.eventTypes, editForm.resolved_event_type_code)}
                            onChange={(selected) => handleResolvedChange("resolved_event_type_code", selected)}
                            placeholder="Select Event Type"
                            isClearable
                        />

                        <label>Resolved File Type</label>
                        <Select
                            options={lookupOptions.fileTypes}
                            value={selectedOption(lookupOptions.fileTypes, editForm.resolved_file_type_code)}
                            onChange={(selected) => handleResolvedChange("resolved_file_type_code", selected)}
                            placeholder="Select File Type"
                            isClearable
                        />

                        <div className="staging-modal-actions">
                            <button className="staging-btn secondary" onClick={closeEdit}>
                                Cancel
                            </button>
                            <button className="staging-btn" onClick={saveEdit}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default StagingImportsPage;
